import type { DocumentDate } from '../types';
import { MONTH_NAMES } from './format';

/**
 * Number of days in a 1-based month.
 *
 * An unknown year defaults to a leap year so Feb 29 stays selectable until the
 * year says otherwise.
 */
export const daysInMonth = (year: number | null, month: number): number =>
    new Date(year ?? 2000, month, 0).getDate();

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Build a DocumentDate from explicit components.
 *
 * Precision is implied by which components are provided: a missing (null /
 * undefined) day gives month precision, a missing month gives year precision.
 * ONLY the known components are stored — no padded month/day and no fabricated
 * ISO `normalized` string — so precision is preserved, never invented.
 */
export const buildDocumentDate = (
    year: number,
    month?: number | null,
    day?: number | null,
): DocumentDate => {
    if (typeof month !== 'number') {
        return { raw: String(year), year, month: null, day: null, precision: 'year' };
    }
    if (typeof day !== 'number') {
        const monthName = MONTH_NAMES[month - 1] ?? '';
        return { raw: `${monthName} ${year}`.trim(), year, month, day: null, precision: 'month' };
    }
    const normalized = `${year}-${pad2(month)}-${pad2(day)}`;
    return { raw: normalized, normalized, year, month, day, precision: 'day' };
};

/** The explicit "document has no date" sentinel. */
export const noDateDocumentDate = (): DocumentDate => ({
    raw: null,
    normalized: undefined,
    year: undefined,
    month: null,
    day: null,
    noDate: true,
});

/**
 * Extract the editable year/month/day components from a stored date, falling
 * back to parsing `normalized` when the explicit components are absent.
 * The no-date sentinel (and a missing date) yields all-null components.
 */
export const documentDateComponents = (
    date?: DocumentDate | null,
): { year: number | null; month: number | null; day: number | null } => {
    if (!date || date.noDate) {
        return { year: null, month: null, day: null };
    }
    if (typeof date.year === 'number') {
        return {
            year: date.year,
            month: typeof date.month === 'number' ? date.month : null,
            day: typeof date.day === 'number' ? date.day : null,
        };
    }
    const match = date.normalized?.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
    }
    return { year: null, month: null, day: null };
};
